import { add, differenceInCalendarDays, format, subDays } from 'date-fns';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DateRange, RangeKeyDict } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

interface JumpToDateRangeControlProps {
    defStartDate: string;
    defEndDate: string;
    onChangeDate: (data: { start_date: string; end_date: string }) => void;
}

const MIN_DAYS = 21;
const MAX_DAYS = 63;

const parseOrDefault = (value: string, fallback: Date) => {
    if (!value) return fallback;
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? fallback : parsed;
};

/**
 * Versi desktop dari "Jump to Date" untuk Kalender Reservasi: popover inline
 * (bukan Modal), tanpa tombol submit, rentang di luar 21-63 hari di-klem
 * otomatis (bukan ditolak lewat alert). Tampilan 2-bulan horizontal
 * dipertahankan sama seperti versi desktop modal sebelumnya. Versi mobile
 * tetap memakai JumpToDateDialogV2 (Modal + tombol Apply + alert), tidak
 * terpengaruh.
 *
 * Deteksi "kedua tanggal sudah dipilih" TIDAK memakai onRangeFocusChange
 * (step tracking wrapper DateRangePicker terbukti tidak reliable saat
 * diuji nyata di browser). Sinyal yang dipakai lebih sederhana & langsung:
 * react-date-range SELALU menyamakan startDate===endDate pada klik pertama
 * (memilih start baru), dan baru membedakan keduanya pada klik kedua
 * (memilih end). Jadi begitu `onChange` melaporkan startDate !== endDate,
 * itu pasti klik KEDUA — rentang sudah lengkap, langsung proses.
 *
 * Popover di-render lewat React Portal ke document.body (posisi dihitung dari
 * getBoundingClientRect() trigger, position:fixed) — BUKAN sebagai absolute
 * div bersarang di dalam pohon DOM pemanggil. Ini menghindari popover
 * "ketimpa"/bertabrakan dengan stacking context milik elemen sticky di
 * bawahnya (mis. header/resource area FullCalendar yang punya z-index
 * sendiri) — pernah terjadi saat dipakai di atas kalender Room Maintenance.
 *
 * Posisi final di-clamp lewat useLayoutEffect (bukan cuma initial guess di
 * openFresh): begitu popover ter-mount, lebar/tinggi ASLINYA diukur lewat
 * getBoundingClientRect() lalu digeser supaya tidak terpotong viewport (kanan
 * atau bawah) — kalau ruang di bawah trigger tidak cukup, popover dibuka ke
 * ATAS trigger. Popover disembunyikan (visibility:hidden) sampai posisi final
 * ini dihitung, supaya tidak ada kedipan "lompat posisi" yang kelihatan user.
 */
const JumpToDateRangeControl: React.FC<JumpToDateRangeControlProps> = ({ defStartDate, defEndDate, onChangeDate }) => {
    const defaultStart = new Date();
    const defaultEnd = add(defaultStart, { days: 30 });

    const [isOpen, setIsOpen] = useState(false);
    const [dateRange, setDateRange] = useState(() => ({
        startDate: parseOrDefault(defStartDate, defaultStart),
        endDate: parseOrDefault(defEndDate, defaultEnd),
    }));
    const [popoverPosition, setPopoverPosition] = useState<{ top: number; left: number } | null>(null);
    const [isPositioned, setIsPositioned] = useState(false);
    // Di layar sempit, 2 bulan horizontal (react-date-range) jauh lebih lebar
    // dari viewport dan tidak bisa di-clamp lagi (sudah di lebar minimalnya) —
    // turunkan jadi 1 bulan vertical, sama seperti pola di RoomMaintenanceFilterComponent.
    const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 576);
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 576);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const containerRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);

    const openFresh = () => {
        // Selalu mulai dari rentang aktif terkini, buang sisa pilihan yang sempat
        // dimulai tapi tidak diselesaikan pada sesi buka sebelumnya.
        setDateRange({
            startDate: parseOrDefault(defStartDate, defaultStart),
            endDate: parseOrDefault(defEndDate, defaultEnd),
        });
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            // Tebakan awal (belum di-clamp) — dikoreksi oleh useLayoutEffect
            // di bawah begitu ukuran asli popover diketahui.
            setPopoverPosition({ top: rect.bottom + 8, left: rect.left });
        }
        setIsPositioned(false);
        setIsOpen(true);
    };

    useLayoutEffect(() => {
        if (!isOpen || !popoverPosition || !popoverRef.current || !triggerRef.current) return;

        const margin = 8;
        const popoverRect = popoverRef.current.getBoundingClientRect();
        const triggerRect = triggerRef.current.getBoundingClientRect();

        let top = triggerRect.bottom + margin;
        let left = triggerRect.left;

        // Clamp horizontal: jangan sampai sisi kanan popover keluar viewport.
        const overflowRight = (left + popoverRect.width) - (window.innerWidth - margin);
        if (overflowRight > 0) {
            left = Math.max(margin, left - overflowRight);
        }

        // Clamp vertical: kalau ruang di bawah trigger tidak cukup, buka ke ATAS trigger.
        const overflowBottom = (top + popoverRect.height) - (window.innerHeight - margin);
        if (overflowBottom > 0) {
            const topAbove = triggerRect.top - margin - popoverRect.height;
            top = topAbove >= margin ? topAbove : Math.max(margin, window.innerHeight - margin - popoverRect.height);
        }

        if (Math.abs(top - popoverPosition.top) > 0.5 || Math.abs(left - popoverPosition.left) > 0.5) {
            setPopoverPosition({ top, left });
        }
        setIsPositioned(true);
    }, [isOpen, popoverPosition]);

    const closePopover = () => {
        setIsOpen(false);
        // Buang preview yang belum selesai; trigger langsung kembali menampilkan rentang aktif sebenarnya.
        setDateRange({
            startDate: parseOrDefault(defStartDate, defaultStart),
            endDate: parseOrDefault(defEndDate, defaultEnd),
        });
    };

    useEffect(() => {
        if (!isOpen) return;
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            // Popover di-portal ke luar containerRef, jadi klik di dalamnya
            // harus dicek terpisah lewat popoverRef — kalau tidak, setiap
            // klik pada popover akan salah dianggap "klik di luar".
            const clickedTrigger = containerRef.current?.contains(target);
            const clickedPopover = popoverRef.current?.contains(target);
            if (!clickedTrigger && !clickedPopover) {
                closePopover();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const applyRange = (s: Date, e: Date) => {
        const dayCount = differenceInCalendarDays(e, s) + 1;

        if (dayCount > MAX_DAYS) {
            s = subDays(e, MAX_DAYS - 1);
        } else if (dayCount < MIN_DAYS) {
            s = subDays(e, MIN_DAYS - 1);
        }

        // Set langsung ke rentang final (sudah diklem) dan tutup TANPA lewat closePopover(),
        // karena closePopover() me-reset dateRange ke defStartDate/defEndDate (props LAMA,
        // yang belum sempat diperbarui oleh onChangeDate di bawah) — kalau lewat situ, hasil
        // klem ini akan langsung tertimpa balik oleh nilai lama pada render yang sama.
        setDateRange({ startDate: s, endDate: e });
        setIsOpen(false);

        onChangeDate({
            start_date: format(s, 'yyyy-MM-dd'),
            end_date: format(e, 'yyyy-MM-dd'),
        });
    };

    const handleDateChange = (rangesByKey: RangeKeyDict) => {
        const { selection } = rangesByKey;
        if (!selection.startDate || !selection.endDate) return;

        const isSameDay = selection.startDate.getTime() === selection.endDate.getTime();

        if (isSameDay) {
            // Klik pertama (pilih start baru) — cuma update tampilan preview, belum selesai.
            setDateRange({ startDate: selection.startDate, endDate: selection.endDate });
            return;
        }

        // startDate !== endDate = klik kedua sudah terjadi, rentang lengkap.
        applyRange(selection.startDate, selection.endDate);
    };

    return (
        <div className="position-relative" ref={containerRef}>
            <button
                ref={triggerRef}
                type="button"
                className="btn btn-sm btn-primary text-white p-2 d-flex align-items-center gap-1"
                onClick={() => (isOpen ? closePopover() : openFresh())}
            >
                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-calendar-event" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                    <path d="M4 5m0 2a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2z"></path>
                    <path d="M16 3l0 4"></path>
                    <path d="M8 3l0 4"></path>
                    <path d="M4 11l16 0"></path>
                    <path d="M8 15h2v2h-2z"></path>
                </svg>
                {format(dateRange.startDate, 'dd MMM yyyy')} - {format(dateRange.endDate, 'dd MMM yyyy')}
            </button>

            {isOpen && popoverPosition && createPortal(
                <div
                    ref={popoverRef}
                    style={{
                        position: 'fixed',
                        zIndex: 2000,
                        top: popoverPosition.top,
                        left: popoverPosition.left,
                        visibility: isPositioned ? 'visible' : 'hidden',
                        maxWidth: 'calc(100vw - 16px)',
                        maxHeight: 'calc(100vh - 16px)',
                        overflow: 'auto',
                        border: '1px solid #dfe2e5',
                        borderRadius: '8px',
                        boxShadow: '0 6px 15px rgba(0, 0, 0, 0.15)',
                        backgroundColor: '#fff',
                    }}
                >
                    <DateRange
                        onChange={handleDateChange}
                        moveRangeOnFirstSelection={false}
                        ranges={[{ ...dateRange, key: 'selection' }]}
                        months={isMobile ? 1 : 2}
                        direction={isMobile ? 'vertical' : 'horizontal'}
                    />
                </div>,
                document.body
            )}
        </div>
    );
};

export default JumpToDateRangeControl;
